import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {AbortController | undefined} */
  #colorRequestController;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('change', this.updateColorPrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('change', this.updateColorPrice);
    this.#colorRequestController?.abort();
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    this.replaceChildren(...Array.from(newProductPrice.childNodes, (node) => node.cloneNode(true)));
  };

  updateColorPrice = (/** @type {Event} */ event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.closest('fieldset[data-option-name="color"]')) return;

    const variantPicker = this.closest('.shopify-section, dialog')?.querySelector('variant-picker');
    if (!(variantPicker instanceof HTMLElement)) return;

    const productUrl = variantPicker.dataset.productUrl;
    const optionValues = Array.from(variantPicker.querySelectorAll('select option[selected], fieldset input:checked'))
      .map((option) => (option instanceof HTMLElement ? option.dataset.optionValueId : undefined))
      .filter(Boolean);

    if (!productUrl || !optionValues.length) return;

    const requestUrl = new URL(productUrl, window.location.origin);
    requestUrl.searchParams.set('option_values', optionValues.join(','));
    this.#colorRequestController?.abort();
    this.#colorRequestController = new AbortController();

    fetch(requestUrl.toString(), { signal: this.#colorRequestController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        this.replaceChildren(...Array.from(newProductPrice.childNodes, (node) => node.cloneNode(true)));
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
